// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * One window holds the call; every other window is a controller.
 *
 * A WebRTC session and a microphone capture belong to one JavaScript context.
 * They cannot be shared between windows, and no browser API moves a live one —
 * so the question is not how to share a call but where it should live. Giving
 * it a window of its own answers several problems at once:
 *
 * - the duplicate-call bug stops being something a guard has to catch, because
 *   there is only one place a call can exist;
 * - "which tab is the call in" has a constant answer, so returning to it needs
 *   a well-known name rather than tracking window identity;
 * - the call survives navigating the app, which a drawer in the main window
 *   does not;
 * - it can be moved to a second monitor, which is what clinicians do with it.
 *
 * **Telephone calls deliberately do not use this.** The call lives on two
 * handsets, the browser is never in the media path, and a window whose only
 * content would be the word "calling" is a window with nothing to show.
 *
 * ## Addressing
 *
 * `window.open(url, CALL_WINDOW_NAME)` opens the window, or targets the
 * existing one if it is already open. Focusing an existing call must pass an
 * **empty** url: any other value navigates that window, which reloads the page
 * and drops the call it was opened to protect.
 */

export const CALL_WINDOW_NAME = 'carepoint-call';

/**
 * The conversation drawer's width.
 *
 * Shared with the call bar, which insets itself by exactly this much so the
 * two never overlap. Mantine's named sizes are a scale rather than a promise,
 * so a literal both sides can read beats two places guessing at 'md'.
 */
export const CONVERSATION_PANEL_WIDTH = 440;

/** Features are advisory — iPad Safari gives a tab, which is fine. */
const CALL_WINDOW_FEATURES = 'popup,width=460,height=680';

export type CallWindowType = 'voice-call' | 'video-call';

export function callWindowUrl(
  patientId: string,
  type: CallWindowType,
  encounterId?: string
): string {
  const params = new URLSearchParams({ patient: patientId, type });
  if (encounterId) {
    params.set('encounter', encounterId);
  }
  return `/call?${params.toString()}`;
}

/**
 * True when this window exists only to show one conversation.
 *
 * Such a window renders the panel as its whole content, so the panel must not
 * be closed there — closing it would leave an empty window rather than tidying
 * anything away.
 */
export function isConversationWindow(): boolean {
  return typeof window !== 'undefined' && window.location.pathname.startsWith('/carecomm');
}

/** True when this window is the one that holds calls. */
export function isCallWindow(): boolean {
  return typeof window !== 'undefined' && window.name === CALL_WINDOW_NAME;
}

/**
 * Bring the call window to the front without disturbing the call.
 *
 * The empty url is load-bearing; see the header. Returns false when no such
 * window exists, which is also what happens after a stale claim — so a dead
 * claim degrades to doing nothing rather than opening a blank window.
 */
export function focusCallWindow(): boolean {
  const existing = window.open('', CALL_WINDOW_NAME);
  if (!existing || existing.closed) {
    return false;
  }
  existing.focus();
  return true;
}

export function openCallWindow(
  patientId: string,
  type: CallWindowType,
  encounterId?: string
): Window | null {
  const opened = window.open(
    callWindowUrl(patientId, type, encounterId),
    CALL_WINDOW_NAME,
    CALL_WINDOW_FEATURES
  );
  opened?.focus();
  return opened;
}

/* ------------------------------------------------------------------ *
 * The claim: what every other window knows about the call
 * ------------------------------------------------------------------ */

const CLAIM_KEY = 'carepoint-active-call';

/**
 * How long a claim outlives its last beat.
 *
 * A window that is killed — crash, force quit, power loss — never clears its
 * claim, so a claim that is merely old must be treated as gone. Generous
 * enough to survive a slow tab being throttled in the background, short
 * enough that a clinician is not told a call is in progress long after it
 * stopped.
 */
export const CLAIM_STALE_AFTER_MS = 30_000;

/** Beat well inside the staleness window, so throttling does not drop it. */
export const CLAIM_BEAT_MS = 10_000;

export interface CallClaim {
  readonly patientId: string;
  /** Shown in the other windows, so the right one can be found by eye. */
  readonly patientName: string;
  readonly type: CallWindowType;
  readonly startedAt: number;
  /**
   * So a controller can show the microphone as it actually is.
   *
   * A mute button that always renders unmuted invites a second press, and the
   * second press is the one that unmutes someone who meant to stay quiet.
   */
  readonly isMuted: boolean;
  /** Same reason as `isMuted`: a control must show the state it toggles. */
  readonly isCameraOff: boolean;
  readonly isScreenSharing: boolean;
  /** Updated by the holding window; absence of beats means it is gone. */
  readonly beatAt: number;
}

/* ------------------------------------------------------------------ *
 * Commands: what a controller window asks the holder to do
 * ------------------------------------------------------------------ */

export type CallCommand =
  | 'toggle-mute'
  | 'toggle-camera'
  | 'toggle-screenshare'
  | 'end-call';

/**
 * BroadcastChannel rather than localStorage, which the claim uses.
 *
 * The two want opposite things. A claim must persist, so a window opened later
 * still learns there is a call. A command must not: replaying "end the call"
 * at whoever opens a tab next is how a later call dies on arrival.
 */
const COMMAND_CHANNEL = 'carepoint-call-command';

/**
 * One channel for the lifetime of the window.
 *
 * Opening one per message and closing it straight after invites the close to
 * race the delivery — the spec lets an implementation discard what is still
 * queued — and a control that works only sometimes is worse than one that does
 * not work at all, because nobody believes the bug report.
 */
let sender: BroadcastChannel | undefined;

/** Ask the window holding the call to do something. */
export function sendCallCommand(command: CallCommand): void {
  if (typeof BroadcastChannel === 'undefined') {
    return;
  }
  sender ??= new BroadcastChannel(COMMAND_CHANNEL);
  sender.postMessage(command);
}

/** Listen for commands. Only the window holding the call should act on them. */
export function subscribeToCallCommands(onCommand: (command: CallCommand) => void): () => void {
  if (typeof BroadcastChannel === 'undefined') {
    return () => undefined;
  }
  /*
   * Its own object, never the sender's. A BroadcastChannel does not deliver to
   * the object that posted, so sharing one would make a window deaf to itself
   * — which works today only because the holder and the controller happen to
   * be different windows, and stops working the moment they are not.
   */
  const bus = new BroadcastChannel(COMMAND_CHANNEL);
  const handler = (event: MessageEvent<CallCommand>): void => onCommand(event.data);
  bus.addEventListener('message', handler);
  return () => {
    bus.removeEventListener('message', handler);
    bus.close();
  };
}

export function readCallClaim(): CallClaim | undefined {
  try {
    const raw = localStorage.getItem(CLAIM_KEY);
    if (!raw) {
      return undefined;
    }
    const claim = JSON.parse(raw) as CallClaim;
    if (Date.now() - claim.beatAt > CLAIM_STALE_AFTER_MS) {
      return undefined;
    }
    return claim;
  } catch {
    // A corrupt claim is no claim; never let it block placing a call.
    return undefined;
  }
}

export function writeCallClaim(claim: Omit<CallClaim, 'beatAt'>): void {
  try {
    localStorage.setItem(CLAIM_KEY, JSON.stringify({ ...claim, beatAt: Date.now() }));
  } catch {
    // Storage full or blocked. The call still works; other windows just will
    // not know about it, which is the pre-existing behaviour.
  }
}

export function clearCallClaim(): void {
  try {
    localStorage.removeItem(CLAIM_KEY);
  } catch {
    // Nothing useful to do; the staleness window will retire it.
  }
}

/**
 * Observe the claim from a window that does not hold the call.
 *
 * Polls as well as listening for `storage`, because that event does not fire
 * for expiry — a claim going stale is the passage of time, not a write, and a
 * crashed holder writes nothing by definition.
 */
export function subscribeToCallClaim(onChange: (claim: CallClaim | undefined) => void): () => void {
  const emit = (): void => onChange(readCallClaim());
  const onStorage = (event: StorageEvent): void => {
    if (event.key === CLAIM_KEY || event.key === null) {
      emit();
    }
  };
  window.addEventListener('storage', onStorage);
  const timer = setInterval(emit, CLAIM_BEAT_MS);
  emit();
  return () => {
    window.removeEventListener('storage', onStorage);
    clearInterval(timer);
  };
}
